import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Plus, Trash2, X, Edit2 } from 'lucide-react';
import { nanoid } from 'nanoid';
import { useVault } from '../../../core/lib/vault';
import {
  type DecryptedDocument,
  upsertDocument,
  deleteDocument,
  getDecryptedDocument,
} from '../sync';
import { compareByTitle } from '../lib/sort';
import { useLiveMeta, updateLiveMeta } from '../lib/yjsCollab';
import {
  ATTACHMENT_DRAG_MIME,
  DOC_UPDATED_EVENT,
  buildAttachmentRef,
  readAttachmentDrag,
  type AttachmentDragPayload,
} from '../lib/attachmentTypes';
import { dragHasFiles, uploadFiles } from '../lib/attachmentUpload';

const TAB_COLORS = [
  '#94a3b8',
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#06b6d4',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
];

interface TabBarProps {
  /** The parent page. Its own content shows as the "Main" tab. */
  page: DecryptedDocument;
  /** Sub-documents — each becomes an additional tab. */
  tabs: DecryptedDocument[];
  /** Currently selected tab id, or null when showing the page itself (Main). */
  selectedTabId: string | null;
  /** Whether the editor is currently editable (i.e. online). Add-tab only shown then. */
  editMode: boolean;
  onSelect: (tabId: string | null) => void;
}

export function TabBar({ page, tabs, selectedTabId, editMode, onSelect }: TabBarProps) {
  const vaultKey = useVault((s) => s.vaultKey);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftColor, setDraftColor] = useState(TAB_COLORS[0]!);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const addPopoverRef = useRef<HTMLDivElement>(null);

  async function handleDrop(target: DecryptedDocument, e: React.DragEvent) {
    e.preventDefault();
    setDropTarget(null);
    if (!vaultKey) return;

    let payloads: AttachmentDragPayload[] = [];
    const inApp = readAttachmentDrag(e);
    if (inApp) {
      payloads = [inApp];
    } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      try {
        payloads = await uploadFiles(e.dataTransfer.files, target.customerId, vaultKey);
      } catch {
        return;
      }
    }
    if (payloads.length === 0) return;

    try {
      const fresh = await getDecryptedDocument(target.id, vaultKey);
      if (!fresh) return;
      let markdown = fresh.markdown;
      for (const p of payloads) {
        const ref = buildAttachmentRef(p);
        const sep = !markdown.trim()
          ? ''
          : markdown.endsWith('\n\n')
            ? ''
            : markdown.endsWith('\n')
              ? '\n'
              : '\n\n';
        markdown = markdown + sep + ref;
      }
      await upsertDocument(
        target.id,
        fresh.customerId,
        fresh.parentId,
        {
          title: fresh.title,
          markdown,
          tags: fresh.tags,
          icon: fresh.icon,
          color: fresh.color,
        },
        vaultKey,
        false,
      );
      window.dispatchEvent(
        new CustomEvent(DOC_UPDATED_EVENT, { detail: { docId: target.id } }),
      );
    } catch {
      /* swallow — surfacing belongs to the central error handler */
    }
  }

  function handleDragOver(targetId: string, e: React.DragEvent) {
    const accept =
      e.dataTransfer.types.includes(ATTACHMENT_DRAG_MIME) || dragHasFiles(e);
    if (!accept) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropTarget(targetId);
  }

  // Close rename popover on click-outside.
  useEffect(() => {
    if (!editingId) return;
    function onClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setEditingId(null);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [editingId]);

  // Close add popover on click-outside.
  useEffect(() => {
    if (!adding) return;
    function onClick(e: MouseEvent) {
      if (addPopoverRef.current && !addPopoverRef.current.contains(e.target as Node)) {
        setAdding(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [adding]);

  function beginAdd() {
    setDraftName('');
    setDraftColor(TAB_COLORS[tabs.length % TAB_COLORS.length]!);
    setAdding(true);
  }

  async function confirmAdd() {
    if (!vaultKey) return;
    const name = draftName.trim() || 'New Tab';
    const id = nanoid();
    await upsertDocument(
      id,
      page.customerId,
      page.id,
      { title: name, markdown: '', color: draftColor },
      vaultKey,
      true,
    );
    setAdding(false);
    // Deliberately do NOT switch to the new tab; the caller stays where they are.
  }

  async function saveTab(t: DecryptedDocument) {
    if (!vaultKey) return;
    const nextTitle = draftName.trim() || t.title || 'Tab';
    // updateLiveMeta before upsertDocument — upsertDocument's merge pulls
    // yjsState (which carries meta) from the live Y.Doc, so meta changes
    // must land in the Y.Doc map first or the outgoing payload carries the
    // stale meta and the next persistSnapshot undoes this rename.
    updateLiveMeta(t.id, { title: nextTitle, color: draftColor });
    await upsertDocument(
      t.id,
      t.customerId,
      t.parentId,
      {
        title: nextTitle,
        markdown: t.markdown,
        tags: t.tags,
        color: draftColor,
      },
      vaultKey,
      false,
    );
    setEditingId(null);
  }

  async function removeTab(t: DecryptedDocument) {
    if (!confirm(`Move tab "${t.title}" to the Orphanage?`)) return;
    await deleteDocument(t.id);
    if (selectedTabId === t.id) onSelect(null);
  }

  function startEdit(t: DecryptedDocument) {
    setEditingId(t.id);
    setDraftName(t.title);
    setDraftColor(t.color ?? TAB_COLORS[0]!);
  }

  // Re-sort live as the active tab's title updates in the Y.Doc meta map —
  // only the open tab has a loaded Y.Doc in this browser, which is the only
  // tab the user can rename here. Cross-browser renames land via pullAll
  // and re-flow through the sort in listDecryptedDocuments.
  const activeLive = useLiveMeta(selectedTabId);
  const sortedTabs = useMemo(() => {
    const liveTitle = activeLive?.title;
    return [...tabs].sort(
      compareByTitle<DecryptedDocument>((t) =>
        selectedTabId && t.id === selectedTabId && liveTitle !== undefined
          ? liveTitle
          : t.title,
      ),
    );
  }, [tabs, selectedTabId, activeLive?.title]);

  return (
    <div className="flex items-stretch gap-1 border-b border-ink-800 bg-ink-900/40 px-2 overflow-x-auto">
      {/* "Main" tab — always shown; represents the page itself */}
      <TabRow
        t={page}
        labelFallback="Main"
        active={selectedTabId === null}
        colorFallback="#64748b"
        isEditing={false}
        dropOver={dropTarget === page.id}
        onSelect={() => onSelect(null)}
        onDragOver={(e) => handleDragOver(page.id, e)}
        onDragLeave={() => setDropTarget((cur) => (cur === page.id ? null : cur))}
        onDrop={(e) => handleDrop(page, e)}
        editMode={false}
        startEdit={() => undefined}
        removeTab={() => undefined}
        popover={null}
      />

      {sortedTabs.map((t) => (
        <TabRow
          key={t.id}
          t={t}
          labelFallback="Tab"
          colorFallback={TAB_COLORS[0]!}
          active={selectedTabId === t.id}
          isEditing={editingId === t.id}
          dropOver={dropTarget === t.id}
          onSelect={() => onSelect(t.id)}
          onDragOver={(e) => handleDragOver(t.id, e)}
          onDragLeave={() => setDropTarget((cur) => (cur === t.id ? null : cur))}
          onDrop={(e) => handleDrop(t, e)}
          editMode={editMode}
          startEdit={() => startEdit(t)}
          removeTab={() => removeTab(t)}
          popover={
            editingId === t.id ? (
              <div
                ref={popoverRef}
                className="absolute z-40 top-full left-0 mt-1 card p-3 shadow-xl"
                style={{ minWidth: 260 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col gap-2">
                  <input
                    className="input !py-1 !text-sm"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveTab(t);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                  />
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex flex-wrap gap-1">
                      {TAB_COLORS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setDraftColor(c)}
                          className={`h-5 w-5 rounded-full ${
                            draftColor === c
                              ? 'ring-2 ring-offset-1 ring-offset-ink-900 ring-white'
                              : ''
                          }`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        className="btn-ghost !p-1"
                        onClick={() => setEditingId(null)}
                      >
                        <X size={14} />
                      </button>
                      <button
                        type="button"
                        className="btn-primary !px-2 !py-1"
                        onClick={() => saveTab(t)}
                      >
                        <Check size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : null
          }
        />
      ))}

      {editMode && (
        <div className="relative flex items-center ml-1">
          <button
            onClick={beginAdd}
            className="btn-ghost !px-2 !py-1 text-xs self-center"
            title="Add tab"
          >
            <Plus size={12} /> Tab
          </button>
          {adding && (
            <div
              ref={addPopoverRef}
              className="absolute z-40 top-full left-0 mt-1 card p-3 shadow-xl"
              style={{ minWidth: 260 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col gap-2">
                <input
                  className="input !py-1 !text-sm"
                  value={draftName}
                  placeholder="Tab name"
                  onChange={(e) => setDraftName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmAdd();
                    if (e.key === 'Escape') setAdding(false);
                  }}
                />
                <div className="flex items-center justify-between gap-1">
                  <div className="flex flex-wrap gap-1">
                    {TAB_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setDraftColor(c)}
                        className={`h-5 w-5 rounded-full ${
                          draftColor === c
                            ? 'ring-2 ring-offset-1 ring-offset-ink-900 ring-white'
                            : ''
                        }`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className="btn-ghost !p-1"
                      onClick={() => setAdding(false)}
                    >
                      <X size={14} />
                    </button>
                    <button
                      type="button"
                      className="btn-primary !px-2 !py-1"
                      onClick={confirmAdd}
                    >
                      <Check size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Renders a single tab, reading live title/colour from the Y.Doc meta map
 * when present (falling back to the decrypted-payload values otherwise).
 */
function TabRow({
  t,
  labelFallback,
  colorFallback,
  active,
  isEditing,
  dropOver,
  onSelect,
  onDragOver,
  onDragLeave,
  onDrop,
  editMode,
  startEdit,
  removeTab,
  popover,
}: {
  t: DecryptedDocument;
  labelFallback: string;
  colorFallback: string;
  active: boolean;
  isEditing: boolean;
  dropOver: boolean;
  onSelect: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  editMode: boolean;
  startEdit: () => void;
  removeTab: () => void;
  popover: React.ReactNode;
}) {
  const live = useLiveMeta(t.id);
  const title = (live?.title ?? t.title) || labelFallback;
  const color = live?.color ?? t.color ?? colorFallback;

  return (
    <div className="relative group flex items-stretch">
      <button
        type="button"
        onClick={onSelect}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm border-b-2 -mb-px ${
          active
            ? 'text-ink-100 border-[var(--tab-color)]'
            : 'text-ink-400 border-transparent hover:text-ink-200'
        } ${dropOver ? 'bg-dragon-500/15 ring-1 ring-inset ring-dragon-500/60' : ''}`}
        style={{ ['--tab-color' as any]: color }}
      >
        <span
          className="h-2 w-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: color }}
        />
        <span className="truncate max-w-[12rem]">{title}</span>
        {editMode && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                startEdit();
              }}
              className="btn-ghost !p-0.5 ml-1 opacity-0 group-hover:opacity-100"
              title="Rename / recolour"
            >
              <Edit2 size={10} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeTab();
              }}
              className="btn-ghost !p-0.5 opacity-0 group-hover:opacity-100"
              title="Delete tab"
            >
              <Trash2 size={10} />
            </button>
          </>
        )}
      </button>
      {isEditing && popover}
    </div>
  );
}
